import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Seo from "@/components/feature/Seo";
import { breadcrumbJsonLd } from "@/lib/seo";
import { pageMeta } from "@/lib/pageMeta";
import { SITE, whatsappLink } from "@/lib/site";

const jsonLd = [
  breadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ]),
];

const channels = [
  {
    icon: "ri-phone-fill",
    title: "Call Us",
    detail: SITE.phoneDisplay,
    note: "Available 24/7, late-night and early-morning calls welcome",
    href: `tel:${SITE.phone}`,
    external: false,
  },
  {
    icon: "ri-whatsapp-fill",
    title: "WhatsApp",
    detail: "Fastest response",
    note: "Instant booking from anywhere in the world",
    href: whatsappLink("Hi! I'd like to book a ride in Los Angeles."),
    external: true,
  },
  {
    icon: "ri-mail-fill",
    title: "Email",
    detail: SITE.email,
    note: "For itineraries, corporate & long-term bookings",
    href: `mailto:${SITE.email}`,
    external: false,
  },
];

export default function Contact() {
  return (
    <PageLayout
      eyebrow="Contact"
      title={
        <>
          Get in Touch
          <br />
          <span className="italic text-foreground-500 font-normal">We Reply Within the Hour</span>
        </>
      }
      intro="Booking from abroad? WhatsApp works from anywhere, in any time zone. For airport pickups, share your flight number and we'll track your arrival."
    >
      <Seo
        title={pageMeta.contact.title}
        description={pageMeta.contact.description}
        path={pageMeta.contact.path}
        jsonLd={jsonLd}
      />
      <section className="px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {channels.map((ch) => (
              <a
                key={ch.title}
                href={ch.href}
                {...(ch.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="bg-background-50 rounded-2xl border border-background-200/60 p-6 hover:border-primary-400 transition-colors group"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${ch.icon} text-xl`} />
                </div>
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-1">
                  {ch.title}
                </h2>
                <p className="text-foreground-800 text-sm font-medium mb-1">{ch.detail}</p>
                <p className="text-foreground-500 text-xs">{ch.note}</p>
              </a>
            ))}
          </div>

          <div className="bg-background-100 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5">
            <div>
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-1.5">
                Prefer to book online?
              </h2>
              <p className="text-foreground-600 text-sm">
                Fill out the reservation form and get a free quote.
              </p>
            </div>
            <Link
              to="/#reservation"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span>Open Booking Form</span>
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { icon: "ri-map-pin-line", label: "Los Angeles, CA" },
              { icon: "ri-time-line", label: "24/7 Service" },
              { icon: "ri-shield-check-line", label: "Licensed & Insured" },
              { icon: "ri-global-line", label: "English Spoken" },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <i className={`${item.icon} text-primary-500 text-xl`} />
                <span className="text-foreground-600 text-xs md:text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
